import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.16 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Ekaa fine dining" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#0D0D0D]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/30 to-[#0D0D0D]/40" />

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.1em" }}
          animate={{ opacity: 1, letterSpacing: "0.4em" }}
          transition={{ duration: 1.2, delay: 0.1 }}
          className="text-[#C8A24A] uppercase text-xs md:text-sm mb-6 md:mb-8"
        >
          Kitchener, Ontario · Fine Indian Dining
        </motion.p>

        <h1 data-testid="hero-heading" className="font-display text-white leading-[0.98] text-[13vw] sm:text-6xl md:text-7xl lg:text-[6.2vw] max-w-6xl">
          {["Experience the Art", "of Indian"].map((t, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {t}
                {i === 1 && <span className="italic text-gold-gradient"> Cuisine</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-7 md:mt-8 max-w-2xl text-[#D6D6D6] font-light text-sm md:text-base leading-relaxed"
        >
          Discover authentic Indian flavors, handcrafted cocktails, premium tandoor
          specialties, and unforgettable dining experiences.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-10 flex flex-col sm:flex-row items-center gap-4"
        >
          <a
            href="#menu"
            data-testid="hero-view-menu"
            className="btn-gold bg-gradient-to-r from-[#C8A24A] to-[#E0C067] text-[#0D0D0D] px-9 py-3.5 rounded-sm text-sm tracking-[0.16em] uppercase font-semibold hover:shadow-[0_0_35px_rgba(200,162,74,0.45)]"
          >
            View Menu
          </a>
          <a
            href="#order"
            data-testid="hero-order-online"
            className="btn-gold border border-[#C8A24A]/70 text-[#C8A24A] px-9 py-3.5 rounded-sm text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#C8A24A]/10"
          >
            Order Online
          </a>
        </motion.div>
      </div>

      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#C8A24A]"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.a>
    </section>
  );
}
